import useFixaLocation from "@coworker/apprestructured/src/shared/wrappers/FixaLocation/useFixaLocation";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { useEffect } from "react";

type SetQueryParamOptions = { replace?: boolean };
type QueryParamValue = string | number | symbol;

type SetQueryParamState<T extends QueryParamValue, TPrevious = T> = (
  valueOrUpdater: T | ((prev: TPrevious) => T),
  options?: SetQueryParamOptions
) => void;

/**
 * Like useState, but the value lives in a url query param, which is the only source of truth.
 * If provided, the default is written when the param is missing on mount (replacing the history entry).
 *
 * Example: useQueryParamState("tab", "overview") <-> /some/path?tab=overview
 */
export function useQueryParamState<T extends QueryParamValue = string>(
  key: string,
  defaultValue: T,
  disable?: boolean
): [T, SetQueryParamState<T>];
export function useQueryParamState<T extends QueryParamValue = string>(
  key: string,
  defaultValue?: undefined,
  disable?: boolean
): [T | undefined, SetQueryParamState<T, T | undefined>];
export function useQueryParamState<T extends QueryParamValue = string>(
  key: string,
  defaultValue?: T,
  disable = false
): [T | undefined, SetQueryParamState<T, T | undefined>] {
  const navigate = useFixaNavigate();
  const { pathname, search } = useFixaLocation();

  const raw = new URLSearchParams(search).get(key);
  // The url is user-editable, so a literal-union T is not guaranteed at runtime.
  const value = (disable ? defaultValue : (raw ?? defaultValue)) as
    T | undefined;

  const writeParam = (nextValue: T, options?: SetQueryParamOptions) => {
    const params = new URLSearchParams(search);
    params.set(key, nextValue.toString());
    navigate(`${pathname}?${params.toString()}`, options);
  };

  const isMissing = raw === null;
  useEffect(() => {
    if (!disable && isMissing && defaultValue !== undefined) {
      writeParam(defaultValue, { replace: true });
    }
    // Only the initial mount should write the default.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setValue: SetQueryParamState<T, T | undefined> = (
    valueOrUpdater,
    options
  ) => {
    if (disable) {
      return;
    }
    const nextValue =
      typeof valueOrUpdater === "function"
        ? valueOrUpdater(value)
        : valueOrUpdater;
    writeParam(nextValue, options);
  };

  return [value, setValue];
}
